import { lazy, Suspense, useEffect, type ComponentType } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom'

import { ScrollToTop } from '@/app/components/ScrollToTop'
import { AdminShellLayout } from '@/features/dashboard/AdminShellLayout'
import { RequireAdmin } from '@/features/dashboard/RequireAdmin'
import { RequireAuth } from '@/features/dashboard/RequireAuth'
import { LocaleLayout } from '@/app/layout/LocaleLayout'
import { LocaleRedirect } from '@/app/layout/LocaleRedirect'
import { NetworkErrorPage } from '@/pages/NetworkErrorPage'
import { NotFoundPage } from '@/pages/NotFoundPage'
import { ServerErrorPage } from '@/pages/ServerErrorPage'

function lazyPage(loader: () => Promise<Record<string, unknown>>, exportName: string) {
  return lazy(async () => {
    const module = await loader()
    return { default: module[exportName] as ComponentType }
  })
}

const AboutPage = lazyPage(() => import('@/pages/AboutPage'), 'AboutPage')
const BlogArticlePage = lazyPage(() => import('@/pages/BlogArticlePage'), 'BlogArticlePage')
const BlogListPage = lazyPage(() => import('@/pages/BlogListPage'), 'BlogListPage')
const AuthForgotPasswordPage = lazyPage(() => import('@/pages/AuthForgotPasswordPage'), 'AuthForgotPasswordPage')
const AuthLoginPage = lazyPage(() => import('@/pages/AuthLoginPage'), 'AuthLoginPage')
const AuthResetPasswordPage = lazyPage(() => import('@/pages/AuthResetPasswordPage'), 'AuthResetPasswordPage')
const ContactPage = lazyPage(() => import('@/pages/ContactPage'), 'ContactPage')
const HomePage = lazyPage(() => import('@/pages/HomePage'), 'HomePage')
const MaintenanceTeamPage = lazyPage(() => import('@/pages/MaintenanceTeamPage'), 'MaintenanceTeamPage')
const ProjectDetailPage = lazyPage(() => import('@/pages/ProjectDetailPage'), 'ProjectDetailPage')
const ProjectsPage = lazyPage(() => import('@/pages/ProjectsPage'), 'ProjectsPage')
const ResourceDetailPage = lazyPage(() => import('@/pages/ResourceDetailPage'), 'ResourceDetailPage')
const ResourcesPage = lazyPage(() => import('@/pages/ResourcesPage'), 'ResourcesPage')
const ServiceDetailPage = lazyPage(() => import('@/pages/ServiceDetailPage'), 'ServiceDetailPage')
const ServicesPage = lazyPage(() => import('@/pages/ServicesPage'), 'ServicesPage')
const SolutionsPage = lazyPage(() => import('@/pages/SolutionsPage'), 'SolutionsPage')

const AnalyticsAdminPage = lazyPage(() => import('@/pages/dashboard/AnalyticsAdminPage'), 'AnalyticsAdminPage')
const ArticleEditPage = lazyPage(() => import('@/pages/dashboard/ArticleEditPage'), 'ArticleEditPage')
const ArticleNewPage = lazyPage(() => import('@/pages/dashboard/ArticleNewPage'), 'ArticleNewPage')
const ArticlesListPage = lazyPage(() => import('@/pages/dashboard/ArticlesListPage'), 'ArticlesListPage')
const ClientLogosAdminPage = lazyPage(() => import('@/pages/dashboard/ClientLogosAdminPage'), 'ClientLogosAdminPage')
const ContentHomePage = lazyPage(() => import('@/pages/dashboard/ContentHomePage'), 'ContentHomePage')
const DashboardHomeEntry = lazyPage(() => import('@/pages/dashboard/DashboardHomeEntry'), 'DashboardHomeEntry')
const FooterAdminPage = lazyPage(() => import('@/pages/dashboard/FooterAdminPage'), 'FooterAdminPage')
const MediaAdminPage = lazyPage(() => import('@/pages/dashboard/MediaAdminPage'), 'MediaAdminPage')
const NavigationAdminPage = lazyPage(() => import('@/pages/dashboard/NavigationAdminPage'), 'NavigationAdminPage')
const SeoAdminPage = lazyPage(() => import('@/pages/dashboard/SeoAdminPage'), 'SeoAdminPage')
const ProfilePage = lazyPage(() => import('@/pages/dashboard/ProfilePage'), 'ProfilePage')
const ProjectCategoriesAdminPage = lazyPage(() => import('@/pages/dashboard/ProjectCategoriesAdminPage'), 'ProjectCategoriesAdminPage')
const ProjectEditPage = lazyPage(() => import('@/pages/dashboard/ProjectEditPage'), 'ProjectEditPage')
const ProjectNewPage = lazyPage(() => import('@/pages/dashboard/ProjectNewPage'), 'ProjectNewPage')
const ProjectViewPage = lazyPage(() => import('@/pages/dashboard/ProjectViewPage'), 'ProjectViewPage')
const ProjectsListPage = lazyPage(() => import('@/pages/dashboard/ProjectsListPage'), 'ProjectsListPage')
const ResourceEditPage = lazyPage(() => import('@/pages/dashboard/ResourceEditPage'), 'ResourceEditPage')
const ResourceNewPage = lazyPage(() => import('@/pages/dashboard/ResourceNewPage'), 'ResourceNewPage')
const ResourceViewPage = lazyPage(() => import('@/pages/dashboard/ResourceViewPage'), 'ResourceViewPage')
const ResourcesListPage = lazyPage(() => import('@/pages/dashboard/ResourcesListPage'), 'ResourcesListPage')
const ServiceEditPage = lazyPage(() => import('@/pages/dashboard/ServiceEditPage'), 'ServiceEditPage')
const ServiceNewPage = lazyPage(() => import('@/pages/dashboard/ServiceNewPage'), 'ServiceNewPage')
const ServiceViewPage = lazyPage(() => import('@/pages/dashboard/ServiceViewPage'), 'ServiceViewPage')
const ServicesListPage = lazyPage(() => import('@/pages/dashboard/ServicesListPage'), 'ServicesListPage')
const SettingsAdminPage = lazyPage(() => import('@/pages/dashboard/SettingsAdminPage'), 'SettingsAdminPage')
const SettingsGooglePage = lazyPage(() => import('@/pages/dashboard/SettingsGooglePage'), 'SettingsGooglePage')
const SettingsTrackingPage = lazyPage(() => import('@/pages/dashboard/SettingsTrackingPage'), 'SettingsTrackingPage')
const UsersAdminPage = lazyPage(() => import('@/pages/dashboard/UsersAdminPage'), 'UsersAdminPage')

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center" role="status" aria-live="polite">
      <span className="h-10 w-10 animate-spin rounded-full border-4 border-slate-200 border-t-slate-700" />
      <span className="sr-only">Loading page</span>
    </div>
  )
}

function RouteRobotsMeta() {
  const { pathname } = useLocation()

  useEffect(() => {
    const isPrivateRoute = pathname.includes('/dashboard') || pathname.includes('/auth/')
    if (!isPrivateRoute) return

    let robots = document.querySelector('meta[name="robots"]') as HTMLMetaElement | null
    if (!robots) {
      robots = document.createElement('meta')
      robots.name = 'robots'
      document.head.appendChild(robots)
    }
    robots.content = 'noindex, nofollow, noarchive'
  }, [pathname])

  return null
}

export function AppRoutes() {
  return (
    <>
      <ScrollToTop />
      <RouteRobotsMeta />
      <Suspense fallback={<RouteFallback />}>
      <Routes>
        <Route path="/" element={<LocaleRedirect />} />
        <Route path="/500" element={<ServerErrorPage />} />
        <Route path="/network-error" element={<NetworkErrorPage />} />
        <Route path="/:locale/auth/login" element={<AuthLoginPage />} />
        <Route path="/:locale/auth/forgot-password" element={<AuthForgotPasswordPage />} />
        <Route path="/:locale/auth/reset-password" element={<AuthResetPasswordPage />} />
        <Route
          path="/:locale/dashboard"
          element={
            <RequireAuth>
              <AdminShellLayout />
            </RequireAuth>
          }
        >
          <Route index element={<DashboardHomeEntry />} />
          <Route path="profile" element={<ProfilePage />} />
          <Route element={<RequireAdmin />}>
            <Route path="content/home" element={<ContentHomePage />} />
            <Route path="articles" element={<ArticlesListPage />} />
            <Route path="articles/new" element={<ArticleNewPage />} />
            <Route path="articles/:articleId/edit" element={<ArticleEditPage />} />
            <Route path="project-categories" element={<ProjectCategoriesAdminPage />} />
            <Route path="projects" element={<ProjectsListPage />} />
            <Route path="projects/new" element={<ProjectNewPage />} />
            <Route path="projects/:projectId/edit" element={<ProjectEditPage />} />
            <Route path="projects/:projectId" element={<ProjectViewPage />} />
            <Route path="services" element={<ServicesListPage />} />
            <Route path="services/new" element={<ServiceNewPage />} />
            <Route path="services/:serviceId/edit" element={<ServiceEditPage />} />
            <Route path="services/:serviceId" element={<ServiceViewPage />} />
            <Route path="resources" element={<ResourcesListPage />} />
            <Route path="resources/new" element={<ResourceNewPage />} />
            <Route path="resources/:resourceId/edit" element={<ResourceEditPage />} />
            <Route path="resources/:resourceId" element={<ResourceViewPage />} />
            <Route path="client-logos" element={<ClientLogosAdminPage />} />
            <Route path="seo" element={<SeoAdminPage />} />
            <Route path="analytics" element={<AnalyticsAdminPage />} />
            <Route path="navigation" element={<NavigationAdminPage />} />
            <Route path="footer" element={<FooterAdminPage />} />
            <Route path="media" element={<MediaAdminPage />} />
            <Route path="settings" element={<SettingsAdminPage />} />
            <Route path="settings/google" element={<SettingsGooglePage />} />
            <Route path="settings/tracking" element={<SettingsTrackingPage />} />
            <Route path="users" element={<UsersAdminPage />} />
          </Route>
        </Route>
        <Route path="/:locale" element={<LocaleLayout />}>
          <Route index element={<HomePage />} />
          <Route path="about" element={<AboutPage />} />
          <Route path="projects" element={<ProjectsPage />} />
          <Route path="projects/:slug" element={<ProjectDetailPage />} />
          <Route path="services" element={<ServicesPage />} />
          <Route path="services/:slug" element={<ServiceDetailPage />} />
          <Route path="maintenance-team" element={<MaintenanceTeamPage />} />
          <Route path="resources" element={<ResourcesPage />} />
          <Route path="resources/:slug" element={<ResourceDetailPage />} />
          <Route path="solutions" element={<SolutionsPage />} />
          <Route path="contact" element={<ContactPage />} />
          <Route path="blog" element={<BlogListPage />} />
          <Route path="blog/:slug" element={<BlogArticlePage />} />
          <Route path="*" element={<NotFoundPage />} />
        </Route>
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
    </>
  )
}
